import ExternalLink from "components/links/ExternalLink";
import React, { PropsWithChildren } from "react";
import { CustomLinkProps } from "types";

/**
 * A version of ExternalLink to use with SVG icons.
 *
 * - It'll properly set the title so that it appears on hover.
 *
 * - The default className is `link-secondary-inverted` to provide hover
 *   behaviour (primary links use underlines to indicate hover, which doesn't
 *   work for pure icon links). But if you wish, the className can be overridden
 *   by passing a new one in the props.
 */
const ExternalIconLink: React.FC<PropsWithChildren<CustomLinkProps>> = ({
    href,
    title,
    className,
    children,
    ...rest
}) => {
    // Add the title to the icon so that it appears on hover. Some of the icons
    // already have a title, but that is not properly cased so we wish to
    // override it with the properly cased name.
    //
    // Setting the title just on the link itself does not work, it needs to be
    // set on the SVG too otherwise the title in the SVG takes preference.
    return (
        <ExternalLink
            href={href}
            title={title}
            aria-label={title}
            className={className ?? "link-secondary-inverted"}
            {...rest}
        >
            {React.Children.map(children, (child) =>
                React.isValidElement(child) ? (
                    React.cloneElement(child as React.ReactElement, {
                        title: title,
                    })
                ) : (
                    <></>
                ),
            )}
        </ExternalLink>
    );
};

export default ExternalIconLink;
